import { missingBrandFields, hasRealText } from './activationProgress.js';
import { normalizeQuickBrandWebsite } from './quickBrand.js';
import { normalizeLang } from '../i18n/index.js';

const ESSENTIALS = ['businessName', 'businessDescription', 'targetAudience', 'offer'];
const unwrap = value => value?.data && typeof value.data === 'object' ? value.data : value;
const clean = value => hasRealText(value) ? value.trim() : '';

export function quickPreviewFields(profile = {}) {
  return {
    businessName: clean(profile?.businessName),
    businessDescription: clean(profile?.businessDescription),
    targetAudience: clean(profile?.targetAudience),
    offer: clean(profile?.offer) || clean(profile?.productOrService),
  };
}

export function missingQuickPreviewFields(profile = {}) {
  return [
    ...(!clean(profile?.businessName) ? ['businessName'] : []),
    ...missingBrandFields(profile || {}).map(key => key === 'offerOrProductOrService' ? 'offer' : key),
  ];
}

/** Website candidates fill blanks; neither saved brand facts nor typed input is replaced. */
export function mergeQuickPreviewScan(fields, candidate) {
  const next = { ...fields };
  const scanned = quickPreviewFields(candidate);
  for (const key of ESSENTIALS) if (!clean(next[key])) next[key] = scanned[key];
  return next;
}

function required() { return Object.assign(new Error('PREVIEW_CONTEXT_REQUIRED'), { code: 'PREVIEW_CONTEXT_REQUIRED' }); }
function changed() { return Object.assign(new Error('PREVIEW_BRAND_CHANGED'), { code: 'PREVIEW_BRAND_CHANGED' }); }

export function quickPreviewReady(fields) {
  return missingQuickPreviewFields(fields).length === 0;
}

/**
 * Profile PUT replaces its payload. Round-trip the fresh profile with a version
 * guard, changing only missing essentials; never send a partial replacement.
 */
export function buildQuickPreviewUpdate(profile, fields, website = '') {
  if (!profile?.id || !profile.updatedAt) throw changed();
  const patch = {};
  for (const key of missingQuickPreviewFields(profile)) patch[key] = clean(fields[key]);
  if (!clean(profile.websiteUrl) && website.trim()) patch.websiteUrl = normalizeQuickBrandWebsite(website);
  const merged = { ...profile, ...patch };
  if (!quickPreviewReady(merged)) throw required();
  if (!Object.keys(patch).length) return null;
  const sourceMetadata = { ...(profile.sourceMetadata || {}) };
  for (const key of Object.keys(patch)) {
    sourceMetadata[key] = { source: 'user_answer', confidence: 1, needsReview: false, updatedAt: new Date().toISOString() };
  }
  return { ...merged, sourceMetadata, brandProfileId: profile.id, expectedUpdatedAt: profile.updatedAt };
}

export async function saveQuickPreviewStart({ apiClient, brandProfile = null, fields, website = '', language = 'en' }) {
  if (!quickPreviewReady({ ...brandProfile, ...fields })) throw required();
  let current = null;
  try {
    const response = unwrap(await apiClient.profile.getBrandProfile({ fresh: true }));
    current = response?.brandProfile || response;
  } catch (error) {
    if (error?.status !== 404) throw error;
  }

  if (brandProfile?.id) {
    if (current?.id !== brandProfile.id) throw changed();
    const body = buildQuickPreviewUpdate(current, fields, website);
    return body ? apiClient.profile.updateBrandProfile(body) : { brandProfile: current };
  }
  // A different tab may have created a brand since this form opened. Do not
  // replay onboarding over that profile or replace any of its new details.
  if (current?.id) throw changed();

  const accepted = quickPreviewFields(fields);
  const websiteUrl = normalizeQuickBrandWebsite(website);
  const sourceMetadata = Object.fromEntries(ESSENTIALS.map(key => [key, {
    source: 'user_answer', confidence: 1, needsReview: false, updatedAt: new Date().toISOString(),
  }]));
  // Same session/answers/complete contract as reviewed quick onboarding. No
  // direct generation: completion owns the existing guarded activation chain.
  await apiClient.onboarding.start();
  await apiClient.onboarding.saveMessage({ stepKey: 'business_name', answerText: accepted.businessName });
  // The message contract rejects blank answers; omission is not a deletion
  // in a reused session. The explicit completion choice below handles that.
  if (websiteUrl) await apiClient.onboarding.saveMessage({ stepKey: 'website', answerText: websiteUrl });
  await apiClient.onboarding.saveMessage({ stepKey: 'language', answerText: normalizeLang(language) || 'en' });
  await apiClient.onboarding.saveMessage({
    stepKey: 'website_analysis',
    question: 'Preview business details reviewed and confirmed',
    answerText: 'Business details confirmed',
    extracted: { ...accepted, sourceMetadata },
  });
  // A second tab can finish setup while these answers are being saved. The
  // legacy complete endpoint reuses a profile, so recheck before invoking it.
  // Keep the early conflict message; the final expectedNoBrand guard below
  // also checks absence atomically at the actual server write.
  try {
    const latest = unwrap(await apiClient.profile.getBrandProfile({ fresh: true }));
    if ((latest?.brandProfile || latest)?.id) throw changed();
  } catch (error) {
    if (error?.status !== 404) throw error;
  }
  return apiClient.onboarding.complete({ expectedNoBrand: true, websiteUrl: websiteUrl || null });
}
